import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, 
  BookOpen, 
  Calendar, 
  FileText, 
  Upload, 
  MessageCircle, 
  CheckCircle, 
  DollarSign,
  User,
  Clock,
  Settings,
  BarChart3,
  Award,
  Bell,
  Building2,
  TrendingUp,
  UserCheck,
  GraduationCap
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';

const InstituteDashboard = () => {
  const { currentUser, userData } = useRealtime();
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalBatches: 0,
    totalCourses: 0,
    monthlyRevenue: 0,
    activeEnrollments: 0
  });

  useEffect(() => {
    // Load institute stats
    if (userData?.instituteProfile) {
      const instituteData = userData.instituteProfile;
      setStats({
        totalStudents: instituteData.students?.length || 0,
        totalTeachers: instituteData.teachers?.length || 0,
        totalBatches: instituteData.batches?.length || 0,
        totalCourses: instituteData.courses?.length || 0,
        monthlyRevenue: instituteData.monthlyRevenue || 0,
        activeEnrollments: instituteData.activeEnrollments || 0
      });
    }
  }, [userData]);

  const managementSections = [
    {
      title: 'Student Management',
      description: 'Manage student enrollments and profiles',
      icon: Users,
      color: 'bg-blue-500',
      link: '/institute/students'
    },
    {
      title: 'Teacher Management',
      description: 'Manage teacher profiles and assignments',
      icon: UserCheck,
      color: 'bg-green-500',
      link: '/institute/teachers'
    },
    {
      title: 'Batch Management',
      description: 'Create and manage batches',
      icon: GraduationCap,
      color: 'bg-purple-500',
      link: '/institute/batches'
    },
    {
      title: 'Course Management',
      description: 'Manage courses and curriculum',
      icon: BookOpen,
      color: 'bg-orange-500',
      link: '/institute/courses'
    },
    {
      title: 'Analytics & Reports',
      description: 'View performance analytics',
      icon: BarChart3,
      color: 'bg-indigo-500',
      link: '/institute/analytics'
    },
    {
      title: 'Financial Management',
      description: 'Manage fees and payments',
      icon: DollarSign,
      color: 'bg-emerald-500',
      link: '/institute/finance'
    }
  ];

  const quickActions = [
    {
      title: 'Add New Student',
      description: 'Enroll a new student',
      icon: Users,
      link: '/institute/students/add'
    },
    {
      title: 'Create Batch',
      description: 'Create a new batch',
      icon: GraduationCap,
      link: '/institute/batches/create'
    },
    {
      title: 'Add Teacher',
      description: 'Add a new teacher',
      icon: UserCheck,
      link: '/institute/teachers/add'
    },
    {
      title: 'View Reports',
      description: 'Generate reports',
      icon: FileText,
      link: '/institute/reports'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-8">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl font-bold text-gray-900 dark:text-white mb-2"
          >
            {userData?.instituteProfile?.instituteName || currentUser?.displayName || 'Institute'} Dashboard 🏫
          </motion.h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage your institute operations and monitor performance
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Students</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalStudents}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Teachers</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalTeachers}</p>
              </div>
              <UserCheck className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Active Batches</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalBatches}</p>
              </div>
              <GraduationCap className="w-8 h-8 text-purple-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Revenue</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">₹{stats.monthlyRevenue.toLocaleString()}</p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500" />
            </div>
          </motion.div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <motion.div
                key={action.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Link
                  to={action.link}
                  className="block bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
                >
                  <action.icon className="w-8 h-8 text-blue-600 mb-4" />
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{action.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{action.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Management Sections */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Institute Management</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {managementSections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
              >
                <Link
                  to={section.link}
                  className="block bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 hover:shadow-md transition-shadow"
                >
                  <div className={`w-12 h-12 ${section.color} rounded-lg flex items-center justify-center mb-4`}>
                    <section.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{section.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{section.description}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Enrollments */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Enrollments</h3>
              <Link to="/institute/students" className="text-blue-600 hover:text-blue-700 text-sm">
                View All
              </Link>
            </div>
            <div className="space-y-3">
              {[1, 2, 3].map((item) => (
                <div key={item} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <User className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">Student Name</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Course Name</p>
                    </div>
                  </div>
                  <span className="text-xs text-gray-500">2 days ago</span>
                </div>
              ))}
            </div>
          </div>

          {/* Performance Overview */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Performance Overview</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Student Satisfaction</span>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                  <span className="text-sm font-medium">85%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Course Completion</span>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div className="bg-blue-500 h-2 rounded-full" style={{ width: '72%' }}></div>
                  </div>
                  <span className="text-sm font-medium">72%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">Teacher Performance</span>
                <div className="flex items-center">
                  <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                    <div className="bg-purple-500 h-2 rounded-full" style={{ width: '90%' }}></div>
                  </div>
                  <span className="text-sm font-medium">90%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstituteDashboard;